import React, { useCallback, useMemo } from 'react';
import { useIdentify, useLocation } from '@shared/core';
import { CatalogStorefront } from '@graphql/generated';
import { createContext } from '@shared/utils/createContext';
import { FUNNEL } from '@shared/core/analytics/telemetry/types';

import { useCatalogRouting } from './useCatalogRouting';
import { useStoredCuration } from './hooks/useStoredCuration';
import { useCatalogRegistryTelemetry } from './Catalog.telemetry';
import { CatalogDepartmentDictionaryEntry } from './state/Catalog';
import { useShopStorefrontContext } from './components/ShopStorefrontProvider';
import { useCategoryMatching } from './hooks/useCategoryMatching/useCategoryMatching';
import { GetProductCatalogCategoryBasePageEventPropsHandler } from './Catalog.logging.types';
import { useTrackCurationVideoPlayer } from './hooks/useTrackCurationVideoPlayer/useTrackCurationVideoPlayer';
import { MinimalProductWithDetails, useCreateAddRegistryItemClickPayload } from './utils/createTelemetryPayload';
import { useTrackCatalogMenuNavigation } from './hooks/useTrackCatalogMenuNavigation/useTrackCatalogMenuNavigation';
import qs from 'query-string';

import {
  AddRegistryMethodType,
  BuyNowClickedProps,
  CatalogPageTelemetry,
  CatalogCategoryType,
  ProductCatalogCategoryProps,
  ItemVariantSelectedProps,
  PagesForTelemetry,
  SearchBarFocusedArgs,
  RegistryValuePropClickedProps,
  AddRegistryItemFromComponent,
  ShopItemClickedFromComponent,
  LoadMoreProductsClickedProps,
  OpenSearchCatalogClickedProps,
  CancelSearchCatalogClickedProps,
  ClearSearchCatalogClickedProps,
  SearchCatalogClickedProps,
  ShopFilterButtonClickedArgs,
  ShopItemClickedProps,
  AddRegistryItemClickedProps,
} from './Catalog.telemetry.types';
import { AddUniversalSearchItemToRegistryPayload, RegistryItemSource } from './Catalog.controller';
import { ExternalProductOffer } from './Catalog.types';
import { universalItemSourceToRegistryItemSource } from './utils/registryItemMetadata';
import { OnFilterSelectArgs } from './components/RefinementFacet/RefinementFacet.types';
import { AddCatalogItemPurchaseMethod } from './components/ProductDetails/components/BuyForm/BuyForm.types';

type TrackCurationVideoPlayerReturnType = ReturnType<typeof useTrackCurationVideoPlayer>;
type TrackCatalogMenuNavigationReturnType = ReturnType<typeof useTrackCatalogMenuNavigation>;

// Catalog logging context
type CatalogLoggingContext = {
  selectedCategory: Maybe<CatalogDepartmentDictionaryEntry>;
  getProductCatalogCategoryBasePageEventProps: GetProductCatalogCategoryBasePageEventPropsHandler;
  trackShopNavigatorClicked: (args: { storefront: CatalogStorefront }) => void;
  trackBuyNowAction: (
    args: {
      product: Maybe<MinimalProductWithDetails>;
      page: CatalogPageTelemetry;
    } & Required<Pick<BuyNowClickedProps, 'method' | 'action'>>,
  ) => void;
  trackItemVariantSelectAction: (args: {
    product: NonNullable<MinimalProductWithDetails>;
    groupDimensionTitle: Maybe<string>;
    groupDimensionOption: Maybe<string>;
    dimensionType: 'dimension' | 'color' | 'image';
    method: ItemVariantSelectedProps['method'];
    dimensionStringValue: Maybe<string>;
    page: CatalogPageTelemetry;
  }) => void;
  trackProductTileClickAction: (
    args: {
      product: MinimalProductWithDetails;
      position: number;
      page: CatalogPageTelemetry;
    } & ShopItemClickedFromComponent,
  ) => void;
  trackAddRegistryItemAction: (
    args: {
      eventId: string;
      itemPosition: Maybe<number>;
      method: AddRegistryMethodType;
      page: CatalogPageTelemetry;
      product: NonNullable<MinimalProductWithDetails>;
      qty: number;
      purchaseMethod?: AddCatalogItemPurchaseMethod | null;
      productCatalogCategoryTypeOverride?: CatalogCategoryType | undefined;
      origin?: string | undefined;
    } & ({} | AddRegistryItemFromComponent),
  ) => void;
  trackCurationClickAction: (args: {
    curation: {
      id: string;
      title: string;
      publicUrlPath: string;
      directItemCount: number;
    };
    page: PagesForTelemetry;
    curationPosition: number;
  }) => void;
  trackLoadMoreProductsButtonClicked: (
    args: Pick<LoadMoreProductsClickedProps, 'endCursor' | 'method' | 'page' | 'productCount' | 'requestSize' | 'startCursor' | 'component'>,
  ) => void;
  trackOpenSearchCatalogClicked: (args: Pick<OpenSearchCatalogClickedProps, 'component' | 'page' | 'searchTerm'>) => void;
  trackCancelSearchCatalogClicked: (args: Pick<CancelSearchCatalogClickedProps, 'component' | 'searchTerm' | 'page'>) => void;
  trackClearSearchCatalogClicked: (args: Pick<ClearSearchCatalogClickedProps, 'component' | 'searchTerm' | 'page'>) => void;
  trackSearchCatalogClicked: (args: Pick<SearchCatalogClickedProps, 'searchTerm' | 'page' | 'method' | 'isUrl'>) => void;
  trackRefinementPanelToggleClicked: (args: Pick<ShopFilterButtonClickedArgs, 'label' | 'method' | 'page'>) => void;
  trackClickShopFilterCheckboxValue: (args: OnFilterSelectArgs) => void;
  trackSearchBarFocused: (args: Pick<SearchBarFocusedArgs, 'page'>) => void;
  trackValuePropTopBarClicked: (title: string) => void;
  trackValuePropClicked: (args: Pick<RegistryValuePropClickedProps, 'method' | 'title' | 'target' | 'productCatalogItemPosition'>) => void;
  trackUniversalSearchResultClickAction: (args: {
    label: 'grid' | 'viewGift';
    component: AddUniversalSearchItemToRegistryPayload['component'];
    searchResult: ExternalProductOffer;
    itemPosition: number;
    gridPlacement: ShopItemClickedProps['gridPlacement'];
    joyCatalogSearchResultCount?: number;
    visibleJoyCatalogSearchResultsCount: number;
    searchTermOverride?: string;
    page?: PagesForTelemetry;
  }) => void;
  trackUniversalSearchInitiated: (args: {
    joyCatalogSearchResultsCount: number;
    visibleJoyCatalogSearchResultsCount: number;
    searchTermOverride?: string;
    page?: PagesForTelemetry;
  }) => void;
  trackAddUniversalSearchItemAction: (
    args: {
      eventId: string;
      itemPosition: number;
      qty: number;
      title: string;
      priceAsFloatingPointDecimalString: string;
      searchTermOverride?: string;
      page?: PagesForTelemetry;
    } & AddUniversalSearchItemToRegistryPayload,
  ) => void;
  trackValuePropDrawerPage: {
    readonly enter: () => void;
  };
  trackBrandListsPage: {
    readonly enter: () => void;
  };
} & TrackCatalogMenuNavigationReturnType &
  TrackCurationVideoPlayerReturnType &
  Pick<
    ReturnType<typeof useCatalogRegistryTelemetry>,
    | 'root'
    | 'department'
    | 'category'
    | 'subcategory'
    | 'curationDetail'
    | 'watchCurationVideoClicked'
    | 'catalogProductsIndexQueryError'
    | 'curationsHeroSectionCarouselItemClicked'
    | 'curationsHeroSectionCarouselArrowClicked'
    | 'curationsHeroSectionExploreCurationClicked'
    | 'curationsHeroSectionBrowseAllClicked'
    | 'toggleRecentlyAddedRegistryItemUpdateDrawerSetting'
    | 'saveRecentlyAddedRegistryItemUpdateDrawerSettings'
  >;

const [Provider, useCatalogLoggingContext] = createContext<CatalogLoggingContext>({ name: 'CatalogLoggingProvider' });

interface CatalogLoggingProviderProps
  extends Readonly<{
    children: React.ReactNode;
    selectedCategory: Maybe<CatalogDepartmentDictionaryEntry>;
  }> {}

/**
 * Track all catalog telemetry events
 * @param children
 * @param selectedCategory
 */
const CatalogLoggingProvider = ({ children, selectedCategory }: CatalogLoggingProviderProps) => {
  const location = useLocation();

  const searchTerm = useMemo(() => {
    const { s: searchQuery } = qs.parse(location.search);
    return typeof searchQuery === 'string' ? searchQuery : '';
  }, [location]);

  const {
    // Page
    valuePropDrawer,
    brandListsPage,

    // Actions
    onRegistryItemAdded,
    addRegistryItemClicked,
    // catalogProductDetailsQueryError,
    // catalogProductsIndexQueryError,
    itemVariantSelected,
    shopCurationClicked,
    shopItemClicked,
    shopNavigateClicked,
    expandMobileShopMenuClick,
    loadMoreProductsClicked,
    buyNowClicked,
    openSearchCatalogClicked,
    cancelSearchCatalogClicked,
    clearSearchCatalogClicked,
    searchCatalogClicked,
    refinementPanelToggleClicked,
    searchBarFocused,
    valuePropClicked,
    valuePropTopBarClicked,
    curationVideoPlaybackStarted,
    curationVideoPlaybackPaused,
    curationVideoPlaybackCompleted,
    curationVideoPlaybackResumed,
    curationVideoContentStarted,
    curationVideoContentPlaying,
    curationVideoContentCompleted,
    shopNavigatorClicked,
    universalSearchInitiated,
    clickFilterCheckboxValue,
    searchTrendsOptionClicked,
    ...rest

    // Pages
  } = useCatalogRegistryTelemetry();

  const { identify } = useIdentify();
  const { currentStorefront } = useShopStorefrontContext();
  const { deriveStandardProductPayload } = useCreateAddRegistryItemClickPayload();
  const { getPublicPathFromContextualUrl, adminRegistrySubRoute } = useCatalogRouting();
  const { getRelatedCuration } = useStoredCuration(null);
  const isOnboardingRoute = adminRegistrySubRoute === 'getting-started';
  const { getCategoryFromUrl, getRelevantCurationOrCategoryForProduct } = useCategoryMatching({ selectedCategory });

  const { trackCategoryNavigateAction, trackExpandMobileShopNavAction } = useTrackCatalogMenuNavigation({
    getCategoryFromUrl,
    getPublicPathFromContextualUrl,
    shopNavigateClicked,
    expandMobileShopMenuClick,
    shopStorefront: currentStorefront,
  });

  const getProductCatalogCategoryBasePageEventProps = useCallback<GetProductCatalogCategoryBasePageEventPropsHandler>(
    ({ curation, product }) => {
      const [category, categoryType] = getRelevantCurationOrCategoryForProduct(product, curation, selectedCategory);
      return {
        category,
        categoryType,
        categoryTelemArgs: isOnboardingRoute
          ? {
              productCatalogCategoryId: undefined,
              productCatalogCategoryPublicUrlPath: undefined,
              productCatalogCategoryTitle: undefined,
              productCatalogCategoryType: undefined,
              shopStorefront: currentStorefront,
            }
          : {
              productCatalogCategoryId: category?.id,
              productCatalogCategoryPublicUrlPath: category?.publicUrlPath,
              productCatalogCategoryTitle: category?.title,
              productCatalogCategoryType: categoryType,
              shopStorefront: currentStorefront,
            },
      };
    },
    [getRelevantCurationOrCategoryForProduct, isOnboardingRoute, currentStorefront, selectedCategory],
  );

  const trackShopNavigatorClicked = useCallback(
    (args: { storefront: CatalogStorefront }) => {
      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product: null });
      shopNavigatorClicked({
        targetShopStorefront: args.storefront,
        ...categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, shopNavigatorClicked],
  );

  const trackCurationVideoHandlers = useTrackCurationVideoPlayer({
    getProductCatalogCategoryBasePageEventProps,
    curationVideoPlaybackStarted,
    curationVideoPlaybackPaused,
    curationVideoPlaybackCompleted,
    curationVideoPlaybackResumed,
    curationVideoContentStarted,
    curationVideoContentPlaying,
    curationVideoContentCompleted,
  });

  const trackBuyNowAction = useCallback(
    (args: { product: Maybe<MinimalProductWithDetails>; page: CatalogPageTelemetry } & Required<Pick<BuyNowClickedProps, 'action' | 'method'>>) => {
      const { action, method, page, product } = args;

      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product });

      buyNowClicked({
        action,
        redirectUrl: product?.productUrl,
        method,
        page,
        productCatalogItemId: product?.id,
        priceValueInMinorUnits: product?.price?.valueInMinorUnits,
        priceCurrencyCode: product?.price?.currency?.code,
        productCatalogBrandId: product?.brand?.id,
        productCatalogBrandTitle: product?.brand?.name,
        itemType: 'gift',
        ...categoryTelemArgs,
      });
    },
    [buyNowClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackItemVariantSelectAction = useCallback(
    (args: {
      product: NonNullable<MinimalProductWithDetails>;
      groupDimensionTitle: Maybe<string>;
      groupDimensionOption: Maybe<string>;
      dimensionType: 'dimension' | 'color' | 'image';
      method: ItemVariantSelectedProps['method'];
      dimensionStringValue: Maybe<string>;
      page: CatalogPageTelemetry;
    }) => {
      const { product, groupDimensionOption, groupDimensionTitle, dimensionStringValue, dimensionType, method, page } = args;
      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product });
      const payload: ItemVariantSelectedProps = {
        page,
        method,
        productCatalogItemId: product.id,
        productCatalogItemTitle: product.title,
        productCatalogItemGroupDimensionOption: groupDimensionOption,
        productCatalogItemGroupDimensionStringType: dimensionType,
        productCatalogItemGroupDimensionTitle: groupDimensionTitle,
        productCatalogItemGroupDimensionString: dimensionStringValue,
        ...categoryTelemArgs,
      };

      itemVariantSelected(payload);
    },
    [getProductCatalogCategoryBasePageEventProps, itemVariantSelected],
  );

  const trackProductTileClickAction = useCallback(
    (
      args: {
        product: MinimalProductWithDetails;
        position: number;
        page: CatalogPageTelemetry;
        // method: 'productTile' | 'productTileSwatch';
      } & ShopItemClickedFromComponent,
    ) => {
      const { page, product, position, ...restArgs } = args;
      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product });
      shopItemClicked({
        page,
        productCatalogItemId: product.id,
        productCatalogItemPosition: position,
        productSource: universalItemSourceToRegistryItemSource(product.source),
        // component,
        // method,
        productCatalogItemTitle: product.title,
        priceValueInMinorUnits: product.price?.valueInMinorUnits,
        priceCurrencyCode: product.price?.currency?.code,
        productCatalogBrandId: product.brand?.id,
        productCatalogBrandTitle: product.brand?.name,
        rating: product.rating?.stats.averageRating || -1,
        reviewCount: product.rating?.stats.totalReviewCount,
        customItemDataType: 'gift',
        searchTerm,
        ...restArgs,
        ...categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, shopItemClicked, searchTerm],
  );

  const trackUniversalSearchResultClickAction = useCallback<CatalogLoggingContext['trackUniversalSearchResultClickAction']>(
    args => {
      const { searchResult, component, joyCatalogSearchResultCount, label, itemPosition, gridPlacement, page, visibleJoyCatalogSearchResultsCount, searchTermOverride } = args;
      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product: null });
      shopItemClicked({
        productCatalogBrandTitle: undefined,
        ...categoryTelemArgs,

        shopStorefront: currentStorefront,
        page: page || 'searchResults',
        searchTerm: searchTermOverride ?? searchTerm,
        url: searchResult?.sellerUrl,
        rating: searchResult?.productRating?.averageRating,
        reviewCount: searchResult?.productRating?.reviewsCount,
        title: searchResult?.productTitle,
        priceValueInMinorUnits: searchResult?.sellerFullPrice.valueInMinorUnits,
        salePriceValueInMinorUnits: searchResult?.sellerSalePrice?.valueInMinorUnits,
        productSource: RegistryItemSource.googleShoppingRegular,
        shopName: searchResult?.sellerName,
        joyCatalogSearchResultCount,
        visibleJoyCatalogSearchResultsCount,
        gridPlacement,
        productCatalogItemPosition: itemPosition,
        customItemDataType: 'gift',
        component,
        method: 'productTile',
        label,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, searchTerm, currentStorefront, shopItemClicked],
  );

  const trackAddUniversalSearchItemAction = useCallback<CatalogLoggingContext['trackAddUniversalSearchItemAction']>(
    args => {
      const { eventId, component, qty, itemPosition, page, priceAsFloatingPointDecimalString, searchTermOverride, ...rest } = args;
      const payload = deriveStandardProductPayload({
        method: 'pdp modal cta',
        eventId,
        qty,
        itemPosition,
        selectedCategory,
        page: page || 'searchResults',
        storedCuration: null,
        product: null,
      });

      const source = universalItemSourceToRegistryItemSource(rest.productSource);
      const fullPayload: AddRegistryItemClickedProps = {
        ...payload,
        component,
        priceValueInMinorUnits: +priceAsFloatingPointDecimalString * 100,
        title: rest.productTitle,
        url: rest.productUrl,
        shopName: rest.shopName,
        productSource: source,
        searchTerm: searchTermOverride ?? payload.searchTerm,
        metadataSource: source,
      };

      addRegistryItemClicked({
        ...fullPayload,
      });
    },
    [addRegistryItemClicked, selectedCategory, deriveStandardProductPayload],
  );

  const trackUniversalSearchInitiated = useCallback<CatalogLoggingContext['trackUniversalSearchInitiated']>(
    ({ joyCatalogSearchResultsCount, visibleJoyCatalogSearchResultsCount, page, searchTermOverride }) => {
      universalSearchInitiated({
        searchTerm: searchTermOverride ?? searchTerm,
        shopStorefront: currentStorefront,
        joyCatalogSearchResultsCount,
        visibleJoyCatalogSearchResultsCount,
        page: page || 'searchResults',
      });
    },
    [currentStorefront, universalSearchInitiated, searchTerm],
  );

  const trackAddRegistryItemAction = useCallback(
    (
      args: {
        eventId: string;
        itemPosition: Maybe<number>;
        method: AddRegistryMethodType;
        page: CatalogPageTelemetry;
        product: NonNullable<MinimalProductWithDetails>;
        purchaseMethod?: AddCatalogItemPurchaseMethod | null;
        qty: number;
        productCatalogCategoryTypeOverride?: CatalogCategoryType;
        origin?: string;
      } & (AddRegistryItemFromComponent | {}),
    ) => {
      const { eventId, method, page, itemPosition, product, qty, productCatalogCategoryTypeOverride, origin, ...rest } = args;

      const isOnboardingRoute = adminRegistrySubRoute === 'getting-started';

      const category = isOnboardingRoute ? `registry.${adminRegistrySubRoute}` : undefined;
      const { productCatalogCategoryType, ...payload } = deriveStandardProductPayload({
        categoryOverride: category,
        method,
        page,
        qty,
        eventId,
        product,
        itemPosition,
        storedCuration: getRelatedCuration(product),
        selectedCategory,
        origin: origin || '',
        isOnboarding: isOnboardingRoute,
      });

      addRegistryItemClicked({ ...payload, ...rest, productCatalogCategoryType: productCatalogCategoryTypeOverride || productCatalogCategoryType });
      identify({ ui_funnel: FUNNEL.Converter, addedJoyGift: true });
      onRegistryItemAdded({ label: `Registry Item ${product.title}`, page });
    },
    [adminRegistrySubRoute, identify, onRegistryItemAdded, addRegistryItemClicked, deriveStandardProductPayload, selectedCategory, getRelatedCuration],
  );

  const trackCurationClickAction = useCallback(
    (args: { curation: { id: string; title: string; publicUrlPath: string; directItemCount: number }; page: PagesForTelemetry; curationPosition: number }) => {
      const { curation, curationPosition, page } = args;
      shopCurationClicked({
        label: curation.title,
        page,
        ...(getProductCatalogCategoryBasePageEventProps({ curation, product: null }).categoryTelemArgs as ProductCatalogCategoryProps<string>),
        curationPosition,
        numItems: curation.directItemCount,
      });
    },
    [shopCurationClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackLoadMoreProductsButtonClicked = useCallback(
    (args: Pick<LoadMoreProductsClickedProps, 'endCursor' | 'method' | 'page' | 'productCount' | 'requestSize' | 'startCursor' | 'component'>) => {
      const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ product: null });
      loadMoreProductsClicked({
        ...args,
        ...categoryTelemArgs,
      });
    },
    [loadMoreProductsClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackOpenSearchCatalogClicked = useCallback(
    (args: Pick<OpenSearchCatalogClickedProps, 'component' | 'page' | 'searchTerm'>) => {
      openSearchCatalogClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, openSearchCatalogClicked],
  );

  const trackCancelSearchCatalogClicked = useCallback(
    (args: Pick<CancelSearchCatalogClickedProps, 'component' | 'searchTerm' | 'page'>) => {
      cancelSearchCatalogClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [cancelSearchCatalogClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackClearSearchCatalogClicked = useCallback(
    (args: Pick<ClearSearchCatalogClickedProps, 'component' | 'searchTerm' | 'page'>) => {
      clearSearchCatalogClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [clearSearchCatalogClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackSearchCatalogClicked = useCallback(
    (args: Pick<SearchCatalogClickedProps, 'searchTerm' | 'page' | 'method'>) => {
      searchCatalogClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, searchCatalogClicked],
  );

  const trackRefinementPanelToggleClicked = useCallback(
    (args: Pick<ShopFilterButtonClickedArgs, 'label' | 'method' | 'page'>) => {
      refinementPanelToggleClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, refinementPanelToggleClicked],
  );

  const trackClickShopFilterCheckboxValue = useCallback(
    (args: OnFilterSelectArgs) => {
      clickFilterCheckboxValue({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, clickFilterCheckboxValue],
  );

  const trackSearchBarFocused = useCallback(
    (args: Pick<SearchBarFocusedArgs, 'page'>) => {
      searchBarFocused({
        ...args,
        label: 'siteHeader',
        component: 'siteHeader',
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [getProductCatalogCategoryBasePageEventProps, searchBarFocused],
  );

  const trackValuePropTopBarClicked = useCallback(
    (title: string) => {
      valuePropTopBarClicked({
        method: 'topBar',
        title,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [valuePropTopBarClicked, getProductCatalogCategoryBasePageEventProps],
  );

  const trackValuePropClicked = useCallback(
    (args: Pick<RegistryValuePropClickedProps, 'method' | 'title' | 'target' | 'productCatalogItemPosition'>) => {
      valuePropClicked({
        ...args,
        ...getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs,
      });
    },
    [valuePropClicked, getProductCatalogCategoryBasePageEventProps],
  );

  // Page Events

  /**
   * The value prop drawer is not necessarily a "page", but we want to track when it is open/closed
   */
  const trackValuePropDrawerPage = useMemo(() => {
    const telemetryArgs = getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs;

    return {
      enter: (): void => valuePropDrawer.enter({ ...telemetryArgs }),
    } as const;
  }, [valuePropDrawer, getProductCatalogCategoryBasePageEventProps]);

  const trackBrandListsPage = useMemo(() => {
    const telemetryArgs = getProductCatalogCategoryBasePageEventProps({ product: null }).categoryTelemArgs;

    return {
      enter: (): void => brandListsPage.enter({ ...telemetryArgs }),
    } as const;
  }, [brandListsPage, getProductCatalogCategoryBasePageEventProps]);

  const catalogLoggingContext = useMemo<CatalogLoggingContext>(() => {
    return {
      selectedCategory,
      getProductCatalogCategoryBasePageEventProps,
      trackShopNavigatorClicked,
      trackBuyNowAction,
      trackItemVariantSelectAction,
      trackProductTileClickAction,
      trackAddRegistryItemAction,
      trackCurationClickAction,
      trackLoadMoreProductsButtonClicked,
      trackOpenSearchCatalogClicked,
      trackCancelSearchCatalogClicked,
      trackClearSearchCatalogClicked,
      trackSearchCatalogClicked,
      trackRefinementPanelToggleClicked,
      trackSearchBarFocused,
      trackValuePropTopBarClicked,
      trackValuePropClicked,
      trackCategoryNavigateAction,
      trackExpandMobileShopNavAction,
      trackUniversalSearchResultClickAction,
      ...trackCurationVideoHandlers,
      trackValuePropDrawerPage,
      trackBrandListsPage,
      trackAddUniversalSearchItemAction,
      trackUniversalSearchInitiated,
      trackClickShopFilterCheckboxValue,
      ...rest,
    };
  }, [
    selectedCategory,
    getProductCatalogCategoryBasePageEventProps,
    trackAddRegistryItemAction,
    trackBuyNowAction,
    trackCancelSearchCatalogClicked,
    trackClearSearchCatalogClicked,
    trackCategoryNavigateAction,
    trackCurationClickAction,
    trackExpandMobileShopNavAction,
    trackItemVariantSelectAction,
    trackLoadMoreProductsButtonClicked,
    trackOpenSearchCatalogClicked,
    trackProductTileClickAction,
    trackSearchCatalogClicked,
    trackUniversalSearchResultClickAction,
    trackSearchBarFocused,
    trackValuePropTopBarClicked,
    trackValuePropClicked,
    trackShopNavigatorClicked,
    trackCurationVideoHandlers,
    trackValuePropDrawerPage,
    trackBrandListsPage,
    trackRefinementPanelToggleClicked,
    trackAddUniversalSearchItemAction,
    trackUniversalSearchInitiated,
    trackClickShopFilterCheckboxValue,
    rest,
  ]);

  return <Provider value={catalogLoggingContext}>{children}</Provider>;
};

export { CatalogLoggingProvider, useCatalogLoggingContext };
